import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Leaf, Loader2, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const formatError = (detail) => {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-stone-50 px-4 py-12 relative overflow-hidden"
      data-testid="login-page"
    >
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-200/40 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-200/30 blur-3xl"
      />

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <span className="h-11 w-11 rounded-xl bg-emerald-700 text-white grid place-items-center">
            <Leaf className="h-6 w-6" />
          </span>
          <div>
            <div className="font-heading text-2xl font-extrabold tracking-tight text-stone-900">
              NutriCal
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-700">
              Maison Thai · Cuisine
            </div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          <h1 className="font-heading text-2xl font-bold text-stone-900 mb-1">
            Connexion
          </h1>
          <p className="text-sm text-stone-500 mb-6">
            Espace réservé à l'équipe cuisine.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[11px] tracking-widest uppercase font-bold text-stone-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cuisine@maison-thai.ch"
                required
                autoComplete="username"
                className="mt-1.5 h-11 border-stone-200 focus-visible:ring-emerald-500 focus-visible:border-emerald-500"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[11px] tracking-widest uppercase font-bold text-stone-600">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 h-11 border-stone-200 focus-visible:ring-emerald-500 focus-visible:border-emerald-500"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
                data-testid="login-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !email || !password}
              className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg"
              data-testid="login-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4 mr-2" />
              )}
              Se connecter
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Maison Thai © {new Date().getFullYear()}. Accès protégé.
        </p>
      </div>
    </div>
  );
}
